// Bulk WhatsApp relance page.
//
// Spec: docs/superpowers/specs/2026-06-04-smart-whatsapp-resume-design.md
//
// Lists every funnel-stuck order with derived sub-state. Admin checks rows,
// clicks "Envoyer les N sélectionnés" — in auto mode we walk the list and
// pop one wa.me/ tab per row with a configurable delay; in manual mode the
// admin clicks per-row.
//
// Templates are editable from this page (saved to app_settings) so admin
// can tweak copy without a deploy. Bulk mode + delay are also editable so
// admin can decide auto-flow vs one-at-a-time confirmation.
//
// Per the user's explicit ask: every row has its own checkbox so they can
// "unselect an exact order if i want to" — there is no fancy bulk-toggle that
// silently includes all visible rows.

import { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { followUpRedirect } from './assistant/assistantUrl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordersApi, settingsApi } from '@/lib/api';
import { useT, type TFunc } from '@/lib/i18n';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { formatPhone } from '@/lib/utils';
import { isRelanceDue } from '@/lib/followUpSchedule';
import { WAITING_ON_US } from '@/lib/followUpStateMachine';
import {
  SUB_STATES_SENDABLE,
  SUB_STATE_LABELS,
  DEFAULT_TEMPLATES,
  renderMessage,
  computeAcompte,
} from '@/lib/followUpMessages';
import { SUB_STATES, albumCountForBundle, type SubState } from '@/lib/followUpStateMachine';

type Row = {
  shopify_order_id: string;
  shopify_order_name: string;
  customer_name: string | null;
  customer_phone: string | null;
  destinations: { 1: string | null; 2: string | null; 3: string | null };
  bundle: string | null;
  status: string;
  created_at: string;
  sub_state: string;
  last_touched_album: number;
  resume_token: string;
  continue_url: string;
  advance_amount: number | null;
  total_price: number | null;
  payment_method: 'cod' | 'virement' | null;
  last_follow_up: {
    sent_at: string;
    sent_by_name: string | null;
    status_at_send: string | null;
    /** Which rung of the ladder was last sent. Absent/null on rows logged
     *  before 2026-08-12, which simply restart at relance 1. */
    attempt?: number | null;
  } | null;
};

type FilterValue = SubState | 'all' | 'recently_followed' | 'due_today';
/** The list's own query key — ONE constant, so an invalidation can never miss it again. */
export const PENDING_FOLLOW_UP_KEY = ['admin-pending-follow-up'] as const;

const SUB_STATE_FILTERS: Array<{ value: FilterValue; label: string }> = [
  // Default view (2026-08-12). Answers the question nothing answered before:
  // "who do I message today?" — 27 of 49 live orders had never been chased
  // once because that list did not exist anywhere.
  { value: 'due_today', label: 'À envoyer aujourd’hui' },
  { value: 'all', label: 'Toutes les relances' },
  { value: 'editor_stuck', label: SUB_STATE_LABELS.editor_stuck },
  { value: 'photos_missing', label: SUB_STATE_LABELS.photos_missing },
  { value: 'album_2_missing', label: SUB_STATE_LABELS.album_2_missing },
  { value: 'cover_not_chosen', label: SUB_STATE_LABELS.cover_not_chosen },
  { value: 'not_started', label: SUB_STATE_LABELS.not_started },
  // The post-preview half of the journey — no filters existed for these at all
  // before 2026-08-12, which is why approved-but-unpaid orders went unchased.
  { value: 'awaiting_preview_feedback', label: SUB_STATE_LABELS.awaiting_preview_feedback },
  { value: 'awaiting_payment', label: SUB_STATE_LABELS.awaiting_payment },
  { value: 'changes_requested', label: SUB_STATE_LABELS.changes_requested },
  { value: 'submitted', label: SUB_STATE_LABELS.submitted },
  { value: 'recently_followed', label: 'Déjà relancé' },
];

// ── Follow-up time helpers (24h / 7d windows match the worker + the tabs) ────
const FOLLOW_UP_RECENT_DAYS = 7;
function hoursSinceIso(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}
function isWithinRecentWindow(iso: string): boolean {
  return hoursSinceIso(iso) < 24 * FOLLOW_UP_RECENT_DAYS;
}
function formatRelativeFollowUp(iso: string, t: TFunc): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return t("À l'instant");
  if (mins < 60) return t('Il y a {n} min', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('Il y a {n} h', { n: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('Hier');
  if (days < 7) return t('Il y a {n} jours', { n: days });
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return t('Il y a {n} sem.', { n: weeks });
  return t('Il y a {n} mois', { n: Math.floor(days / 30) });
}
// Tone for the "Dernière relance" cell: green < 24h, orange < 7d, grey older/never.
function followUpTone(iso: string | null): { dot: string; text: string } {
  if (!iso) return { dot: 'bg-gray-200', text: 'text-gray-300' };
  const h = hoursSinceIso(iso);
  if (h < 24) return { dot: 'bg-green-500', text: 'text-green-700' };
  if (h < 24 * 7) return { dot: 'bg-orange-400', text: 'text-orange-600' };
  return { dot: 'bg-gray-300', text: 'text-gray-500' };
}

/**
 * /admin/follow-up — Relances now live in ONE place (owner plan, 2026-10-01):
 * WhatsApp Assistant ▸ Automatisations ▸ Relances, beside the agent's rules.
 * An admin is redirected there (its ?filter= kept). Staff whose role grants
 * `followups.view` cannot open the Assistant (admin-only), so for them this
 * page still shows the same list — a redirect would bounce them to « / ».
 */
export default function AdminFollowUpPage() {
  const { t } = useT();
  const { profile } = useAuthStore();
  const location = useLocation();
  if (profile?.role === 'admin') return <Navigate to={followUpRedirect(location.search)} replace />;
  return (
    <AdminLayout title={t('Relances WhatsApp')}>
      <ManualReminders initialFilter={new URLSearchParams(location.search).get('filter')} />
    </AdminLayout>
  );
}

const isFilter = (v: string | null | undefined): v is FilterValue => SUB_STATE_FILTERS.some((f) => f.value === v);

/** The manual reminder list (the old page's content), shown inside Automatisations ▸ Relances. */
export function ManualReminders({ initialFilter = null }: { initialFilter?: string | null } = {}) {
  const { t } = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: PENDING_FOLLOW_UP_KEY,
    queryFn: () => ordersApi.pendingFollowUp(),
    staleTime: 60_000,
  });

  // Settings drive templates + bulk mode + delay. Single shared source of
  // truth so the single-order SmartFollowUpCard sees the same values.
  const { data: settingsData } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 5 * 60_000,
  });
  const settings = settingsData?.settings;
  const customTemplates = settings?.follow_up_templates ?? null;
  const bulkMode = settings?.follow_up_bulk_mode ?? 'auto';
  const bulkDelayMs = settings?.follow_up_bulk_delay_ms ?? 1000;
  // Default acompte (MAD) when an order has no per-order override. Mirrors the
  // AMEEX delivery card's fallback so both surfaces agree on the amount.
  const defaultAdvance = Math.round(Number(settings?.advance_amount ?? 50));

  const queryClient = useQueryClient();

  // ─── IGNORED: THE LIST, AND THE WAY BACK ────────────────────────────────
  //
  // "Somewhere I can see everything ignored, so it is not a black hole."
  // Right under the waiting list, because the two are the same decision seen
  // from opposite sides.
  const { data: ignoredData } = useQuery({
    queryKey: ['ignored-list'],
    queryFn: () => ordersApi.ignored(),
    staleTime: 30_000,
  });
  const ignoredItems = ignoredData?.items ?? [];
  const refreshBoth = () => {
    queryClient.invalidateQueries({ queryKey: ['ignored-list'] });
    // ⚠️ THE LIST'S REAL KEY. This was ['pending-follow-up'], which matches
    // nothing: an ignored order stayed on screen until the next refetch.
    queryClient.invalidateQueries({ queryKey: PENDING_FOLLOW_UP_KEY });
  };
  const ignoreRow = async (shopifyOrderId: string) => {
    const reason = window.prompt(t(
      'Ignorer cette commande ?\n\n'
      + '• On arrête TOUTES les relances.\n'
      + '• Elle sort de cette liste.\n'
      + "• L'assistant répond quand même si le client écrit.\n\n"
      + "La commande n'est PAS annulée. Réversible.\n\nRaison (facultatif) :",
    ), '');
    if (reason === null) return;
    await ordersApi.ignore(shopifyOrderId, reason);
    refreshBoth();
  };
  const [filter, setFilter] = useState<FilterValue>(() => (isFilter(initialFilter) ? initialFilter : 'due_today'));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Per-row "sent" markers — purely local. Cleared on page reload, which is
  // fine: admin only needs in-session tracking so they don't accidentally
  // re-send the same customer twice in a sitting.
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const allRows: Row[] = data?.orders ?? [];
  const rows = useMemo(() => {
    if (filter === 'all') return allRows;
    if (filter === 'recently_followed')
      return allRows.filter((r) => r.last_follow_up && isWithinRecentWindow(r.last_follow_up.sent_at));
    if (filter === 'due_today') {
      // Owed a relance right now: J+1 from the order, then J+3 / J+7 / J+14.
      // Orders we owe work on (changes_requested) are excluded — chasing a
      // client for something WE have not done yet is the wrong message.
      return allRows
        .filter((r) => !WAITING_ON_US.has(r.sub_state))
        .filter((r) => isRelanceDue({
          anchorAt: r.created_at,
          lastSentAt: r.last_follow_up?.sent_at ?? null,
          attemptsSent: r.last_follow_up ? (r.last_follow_up.attempt ?? 1) : 0,
        }))
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));   // oldest first
    }
    return allRows.filter((r) => r.sub_state === filter);
  }, [allRows, filter]);

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAllVisible = () => {
    setSelected(new Set(
      rows
        .filter((r) => r.customer_phone && SUB_STATES_SENDABLE.has(r.sub_state as SubState))
        .map((r) => r.shopify_order_id),
    ));
  };
  const clearSelection = () => setSelected(new Set());

  // Fire-and-forget: persist the send to follow_up_history so other admins see
  // it. Never blocks the wa.me open; on success we refetch so the "Dernière
  // relance" column + "Déjà relancé" tab update for everyone.
  const recordSend = (id: string, subState: string) => {
    void ordersApi
      .recordFollowUp(id, subState)
      .then(() => queryClient.invalidateQueries({ queryKey: PENDING_FOLLOW_UP_KEY }))
      .catch(() => {});
  };

  // Single-row send. needsConfirm = client already relancé < 24h ago, so we ask
  // before re-sending (the button still works — just guarded).
  const doSend = (r: Row, needsConfirm: boolean) => {
    if (needsConfirm && !window.confirm(t("Ce client a déjà été relancé aujourd'hui. Envoyer quand même ?"))) return;
    window.open(buildWaUrl(r, customTemplates, defaultAdvance), '_blank', 'noopener,noreferrer');
    setSent((prev) => new Set(prev).add(r.shopify_order_id));
    recordSend(r.shopify_order_id, r.sub_state);
  };

  // Open wa.me/ tabs for selected rows. In auto mode: loop with delay. In
  // manual mode: open the first tab, then keep selection so admin clicks
  // "Envoyer N" again to advance (one-at-a-time confirmation flow).
  const sendSelected = async () => {
    const selectedRows = rows.filter((r) => selected.has(r.shopify_order_id));
    if (bulkMode === 'manual') {
      const next = selectedRows[0];
      if (!next || !next.customer_phone) return;
      window.open(buildWaUrl(next, customTemplates, defaultAdvance), '_blank', 'noopener,noreferrer');
      setSent((prev) => new Set(prev).add(next.shopify_order_id));
      recordSend(next.shopify_order_id, next.sub_state);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(next.shopify_order_id);
        return n;
      });
      return;
    }
    for (const r of selectedRows) {
      if (!r.customer_phone) continue;
      window.open(buildWaUrl(r, customTemplates, defaultAdvance), '_blank', 'noopener,noreferrer');
      setSent((prev) => new Set(prev).add(r.shopify_order_id));
      recordSend(r.shopify_order_id, r.sub_state);
      await new Promise((resolve) => setTimeout(resolve, bulkDelayMs));
    }
    clearSelection();
  };

  return (
      <div className="space-y-0">
        <p className="text-[12.5px] text-text-secondary mb-3 tabular-nums">
          {isLoading
            ? t('Chargement…')
            : allRows.length === 1
              ? t('{n} commande en attente de relance', { n: allRows.length })
              : t('{n} commandes en attente de relance', { n: allRows.length })}
        </p>
        {/* Bulk mode + delay + template editor controls */}
        <BulkConfigBar
          bulkMode={bulkMode}
          bulkDelayMs={bulkDelayMs}
          onToggleTemplates={() => setTemplatesOpen((v) => !v)}
          templatesOpen={templatesOpen}
        />

        {templatesOpen && (
          <TemplatesEditor
            initialTemplates={customTemplates}
            onClose={() => setTemplatesOpen(false)}
          />
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap gap-2">
            {SUB_STATE_FILTERS.map((f) => {
              const count = f.value === 'all'
                ? allRows.length
                : f.value === 'recently_followed'
                  ? allRows.filter((r) => r.last_follow_up && isWithinRecentWindow(r.last_follow_up.sent_at)).length
                  : allRows.filter((r) => r.sub_state === f.value).length;
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => { setFilter(f.value); clearSelection(); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    active
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {t(f.label)} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl px-4 py-2.5">
            <div className="text-sm text-gray-800">
              <strong>{selected.size}</strong>{' '}
              {selected.size > 1 ? t('commandes sélectionnées') : t('commande sélectionnée')}
              {bulkMode === 'auto' && t(' · auto · délai {s}s', { s: (bulkDelayMs / 1000).toFixed(1) })}
              {bulkMode === 'manual' && t(' · manuel (une à la fois)')}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                {t('Effacer la sélection')}
              </Button>
              <button
                onClick={sendSelected}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#25D366] text-white hover:bg-[#1ebe5a] dark:bg-status-done-solid dark:hover:bg-[#1C6E3D]"
              >
                {bulkMode === 'manual'
                  ? t('Envoyer le suivant')
                  : t('Envoyer les {n} sélectionnés', { n: selected.size })}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-center text-sm text-red-600">
              {t('Erreur lors du chargement des relances.')}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">🎉</div>
              <p className="text-gray-500 text-sm">{t('Aucune commande à relancer dans cette catégorie.')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        aria-label={t('Sélectionner toutes les lignes visibles')}
                        checked={rows.length > 0 && rows.every((r) => selected.has(r.shopify_order_id))}
                        onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Commande')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">{t('Client')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Téléphone')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Bloqué à')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Dernière relance')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">{t('Bundle')}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((r) => {
                    const isSelected = selected.has(r.shopify_order_id);
                    const canSend = !!r.customer_phone && SUB_STATES_SENDABLE.has(r.sub_state as SubState);
                    const lastAt = r.last_follow_up?.sent_at ?? null;
                    // "Recently" = relancé < 24h ago, OR sent in this session.
                    const followedRecently =
                      sent.has(r.shopify_order_id) || (lastAt != null && hoursSinceIso(lastAt) < 24);
                    return (
                      <tr key={r.shopify_order_id} className={followedRecently ? 'bg-green-50/40' : 'hover:bg-gray-50/50'}>
                        <td className="px-3 py-3 align-middle">
                          <input
                            type="checkbox"
                            aria-label={t('Sélectionner {name}', { name: r.shopify_order_name })}
                            checked={isSelected}
                            onChange={() => toggleRow(r.shopify_order_id)}
                            disabled={!canSend}
                            className="rounded border-gray-300 disabled:opacity-30"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle hidden md:table-cell">
                          <div className="font-mono text-xs text-gray-700">{r.shopify_order_name}</div>
                        </td>
                        <td className="px-2 md:px-4 py-3 align-middle">
                          <div className="font-medium text-gray-800">{r.customer_name ?? '—'}</div>
                          {/* On a phone the order and the stage ride under the name — no sideways table. */}
                          <div className="md:hidden mt-0.5 flex flex-wrap items-center gap-1.5">
                            <span className="font-mono text-xs text-gray-600">{r.shopify_order_name}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${badgeColorFor(r.sub_state)}`}>
                              {SUB_STATE_LABELS[r.sub_state as SubState] ? t(SUB_STATE_LABELS[r.sub_state as SubState]) : r.sub_state}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle hidden md:table-cell">
                          <div className="text-gray-600 font-mono text-xs">
                            {r.customer_phone ? formatPhone(r.customer_phone) : <span className="text-red-500">{t('aucun')}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle hidden md:table-cell">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${badgeColorFor(r.sub_state)}`}>
                            {SUB_STATE_LABELS[r.sub_state as SubState] ? t(SUB_STATE_LABELS[r.sub_state as SubState]) : r.sub_state}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle hidden md:table-cell">
                          {r.last_follow_up ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${followUpTone(r.last_follow_up.sent_at).dot}`} />
                              <div className="leading-tight">
                                <div className={`text-xs font-medium ${followUpTone(r.last_follow_up.sent_at).text}`}>
                                  {formatRelativeFollowUp(r.last_follow_up.sent_at, t)}
                                </div>
                                {r.last_follow_up.sent_by_name && (
                                  <div className="text-[10px] text-gray-400 truncate max-w-[120px]">{r.last_follow_up.sent_by_name}</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-200" />
                              {t('Jamais')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle hidden lg:table-cell">
                          <span className="text-xs text-gray-500">{r.bundle ?? '—'}</span>
                        </td>
                        <td className="pl-1 pr-3 md:px-4 py-3 align-middle text-right whitespace-nowrap">
                          {!canSend ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : followedRecently ? (
                            <button
                              onClick={() => doSend(r, true)}
                              title={t('Déjà relancé récemment — cliquer pour renvoyer')}
                              className="inline-flex items-center gap-1 px-2.5 py-1 min-h-[44px] md:min-h-0 rounded-lg text-xs font-medium border border-gray-300 text-gray-500 hover:bg-gray-50"
                            >
                              ↻ {t('Renvoyé')}
                            </button>
                          ) : (
                            <button
                              onClick={() => doSend(r, false)}
                              className="px-2.5 py-1 min-h-[44px] md:min-h-0 rounded-lg text-xs font-semibold bg-[#25D366] text-white hover:bg-[#1ebe5a] dark:bg-status-done-solid dark:hover:bg-[#1C6E3D]"
                            >
                              {t('Envoyer')}
                            </button>
                          )}
                          {/* Stop chasing THIS one, from the list itself — the
                              place you are standing when you decide it. */}
                          <button
                            onClick={() => ignoreRow(r.shopify_order_id)}
                            title={t('Arrêter les relances pour cette commande')}
                            className="ml-2 px-2 py-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 rounded-lg text-xs border border-gray-300 text-gray-500 hover:bg-gray-50"
                          >
                            🔕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ─── EVERYTHING IGNORED ───────────────────────────────────────────
            A decision to stop chasing has to stay visible, or it becomes a
            quiet grave: rows nobody sees, nobody reviews and nobody undoes.
            Who pressed it and when are shown, and one click puts it back. */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-1">
            {t('🔕 Ignorées ({n})', { n: ignoredItems.length })}
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            {t('Aucune relance ne part pour celles-ci. Rien n\'est annulé ni supprimé, '
              + "et l'assistant répond toujours si le client écrit.")}
          </p>
          {ignoredItems.length === 0 ? (
            <p className="text-xs text-gray-400">{t('Rien n\'est ignoré.')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {ignoredItems.map((i) => (
                <li key={i.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-gray-700">
                      {i.order?.shopify_order_name ?? (i.kind === 'chat' ? t('Conversation') : i.ref)}
                      {i.order?.customer_name ? ` · ${i.order.customer_name}` : ''}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {i.reason ? `« ${i.reason} » · ` : ''}
                      {t('par {who}, le {when}', {
                        who: i.ignored_by ?? '—',
                        when: new Date(i.ignored_at).toLocaleDateString(),
                      })}
                    </div>
                  </div>
                  {i.kind === 'order' && i.order ? (
                    <button
                      onClick={async () => {
                        await ordersApi.unignore(i.order!.shopify_order_name);
                        refreshBoth();
                      }}
                      className="shrink-0 px-2.5 py-1 rounded-lg text-xs border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      {t('Reprendre le suivi')}
                    </button>
                  ) : (
                    <span className="shrink-0 text-[11px] text-gray-400">{t('conversation')}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
  );
}

function badgeColorFor(subState: string): string {
  if (subState === 'editor_stuck') return 'bg-orange-50 text-orange-700 border border-orange-200';
  if (subState === 'submitted' || subState === 'in_transit' || subState === 'delivered' || subState === 'printed_not_shipped') {
    return 'bg-gray-50 text-gray-600 border border-gray-200';
  }
  return 'bg-amber-50 text-amber-700 border border-amber-200';
}

function buildWaUrl(r: Row, customTemplates: Record<string, string> | null, defaultAdvance: number): string {
  const firstName = (r.customer_name ?? '').trim().split(/\s+/)[0] ?? '';
  const destination = r.destinations[r.last_touched_album as 1 | 2 | 3] ?? '';
  // Acompte = base advance × number of albums (Solo 50 / Duo 100 / Trio 150).
  const albumCount = albumCountForBundle(r.bundle as 'Solo' | 'Duo' | 'Trio' | null);
  const acompte = computeAcompte(defaultAdvance, albumCount);
  // All album destinations, in order, for the Album Soumis message list.
  const destinationsList = ([r.destinations[1], r.destinations[2], r.destinations[3]] as (string | null)[])
    .slice(0, albumCount)
    .map((d) => (d ?? '').toString().trim())
    .filter(Boolean)
    .join(', ');
  const message = renderMessage(r.sub_state as SubState, {
    firstName,
    destination,
    destinationsList,
    link: r.continue_url,
    acompte,
    total: r.total_price != null ? Math.round(Number(r.total_price)) : undefined,
  }, customTemplates);
  const digits = (r.customer_phone ?? '').replace(/\D/g, '');
  const intl = digits.startsWith('0') ? `212${digits.slice(1)}` : digits;
  return `https://wa.me/${intl}?text=${encodeURIComponent(message)}`;
}

// ─── Bulk config bar ────────────────────────────────────────────────────────
// Auto/manual mode + delay control. Saved to app_settings on change so the
// single-order card and bulk page share the same values across sessions.
function BulkConfigBar({
  bulkMode,
  bulkDelayMs,
  onToggleTemplates,
  templatesOpen,
}: {
  bulkMode: 'auto' | 'manual';
  bulkDelayMs: number;
  onToggleTemplates: () => void;
  templatesOpen: boolean;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (data: Partial<{ follow_up_bulk_mode: 'auto' | 'manual'; follow_up_bulk_delay_ms: number }>) =>
      settingsApi.update(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-4">
      {/* Mode toggle */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">{t("Mode d'envoi")}</span>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => save.mutate({ follow_up_bulk_mode: 'auto' })}
            className={`px-3 py-1.5 text-xs font-medium ${
              bulkMode === 'auto' ? 'bg-brand-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('Auto')}
          </button>
          <button
            onClick={() => save.mutate({ follow_up_bulk_mode: 'manual' })}
            className={`px-3 py-1.5 text-xs font-medium ${
              bulkMode === 'manual' ? 'bg-brand-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('Manuel')}
          </button>
        </div>
      </div>

      {/* Delay control — only meaningful in auto mode but always shown so admin
           sees the current value. */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-gray-500">{t('Délai entre envois')}</label>
        <input
          type="number"
          min={0}
          max={10000}
          step={250}
          defaultValue={bulkDelayMs}
          onBlur={(e) => {
            const v = Math.max(0, Math.min(10000, Number(e.target.value) || 0));
            if (v !== bulkDelayMs) save.mutate({ follow_up_bulk_delay_ms: v });
          }}
          disabled={bulkMode === 'manual'}
          className="w-24 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50 disabled:text-gray-400"
        />
        <span className="text-xs text-gray-500">ms</span>
      </div>

      {/* Templates editor toggle */}
      <button
        onClick={onToggleTemplates}
        className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50"
      >
        {templatesOpen ? t("Fermer l'éditeur") : '✏️ ' + t('Personnaliser les messages')}
      </button>
    </div>
  );
}

// ─── Templates editor ───────────────────────────────────────────────────────
// One textarea per SubState. Placeholders {firstName}, {destination}, {link}
// are documented in the header — they're plain string replaces at render
// time, so no escaping needed. Saving merges into app_settings.follow_up_templates.
function TemplatesEditor({
  initialTemplates,
  onClose,
}: {
  initialTemplates: Record<string, string> | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const key of Object.keys(SUB_STATES) as SubState[]) {
      initial[key] = initialTemplates?.[key] ?? DEFAULT_TEMPLATES[key];
    }
    return initial;
  });

  const save = useMutation({
    mutationFn: () => settingsApi.update({ follow_up_templates: drafts }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      onClose();
    },
  });

  const reset = (key: SubState) =>
    setDrafts((prev) => ({ ...prev, [key]: DEFAULT_TEMPLATES[key] }));

  const resetAll = useMutation({
    mutationFn: () => settingsApi.update({ follow_up_templates: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      const fresh: Record<string, string> = {};
      for (const key of Object.keys(SUB_STATES) as SubState[]) fresh[key] = DEFAULT_TEMPLATES[key];
      setDrafts(fresh);
    },
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{t('Messages personnalisés')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('Espaces réservés :')} <code className="bg-gray-100 px-1 rounded">{`{firstName}`}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{`{destination}`}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{`{link}`}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => resetAll.mutate()} disabled={resetAll.isPending}>
            {t('Réinitialiser tout')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>{t('Fermer')}</Button>
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
            {t('Enregistrer')}
          </Button>
        </div>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {(Object.keys(SUB_STATES) as SubState[]).map((key) => {
          const isCustom = drafts[key] !== DEFAULT_TEMPLATES[key];
          return (
            <div key={key} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700">{t(SUB_STATE_LABELS[key])}</span>
                  {isCustom && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">{t('modifié')}</span>}
                </div>
                <button
                  onClick={() => reset(key)}
                  className="text-[11px] text-gray-400 hover:text-gray-700 underline"
                >
                  {t('Restaurer le message par défaut')}
                </button>
              </div>
              <textarea
                value={drafts[key]}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 whitespace-pre-wrap"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
